/**
 * Cross-game signal aggregation — the Sprint 2 keystone.
 *
 * Sprint 1 computed signals per game; this rolls the stored play-by-play (`AtBatDoc`)
 * across ALL of a team's games into per-player season signals, and turns the batting
 * signals into a *suggested* batting order (real on-base + patience, not OVR guesses).
 *
 * Pure + parser-independent: operates on AtBatDoc[]. The order is a SUGGESTION in the
 * bench-coach voice — it proposes; the head coach sets the lineup.
 */

import { AtBatDoc } from '../../types/dugout';

const HIT = new Set(['1B', '2B', '3B', 'HR']);
const XBH = new Set(['2B', '3B', 'HR']);
const NON_AB = new Set(['BB', 'HBP', 'SF', 'SAC']);

export interface BatterSeasonSignal {
  playerId: string;
  playerName: string;
  games: number;
  pa: number;
  ab: number;
  reached: number;
  onBasePct: number;     // reached / PA — the real on-base rate
  hits: number;
  bb: number;
  so: number;
  xbh: number;
  pitchesSeen: number;
  pitchesPerPa: number;  // patience
}

/** Roll our matched batters' at-bats (across all games) into per-player signals. */
export function aggregateBatterSignals(atBats: AtBatDoc[]): Map<string, BatterSeasonSignal> {
  const ours = atBats.filter(ab => ab.batterTeam === 'us' && ab.batterId && ab.confidence !== 'low');
  const groups = new Map<string, AtBatDoc[]>();
  for (const ab of ours) {
    const k = ab.batterId!;
    (groups.get(k) ?? groups.set(k, []).get(k)!).push(ab);
  }

  const out = new Map<string, BatterSeasonSignal>();
  for (const [pid, abs] of groups) {
    const pa = abs.length;
    const ab = abs.filter(a => !NON_AB.has(a.resultCode)).length;
    const reached = abs.filter(a => a.reachedBase).length;
    const hits = abs.filter(a => HIT.has(a.resultCode)).length;
    const bb = abs.filter(a => a.resultCode === 'BB').length;
    const so = abs.filter(a => a.resultCode === 'K').length;
    const xbh = abs.filter(a => XBH.has(a.resultCode)).length;
    const pitchesSeen = abs.reduce((s, a) => s + (a.pitchCount || a.pitchSequence.length), 0);
    const games = new Set(abs.map(a => a.gameId)).size;
    out.set(pid, {
      playerId: pid, playerName: abs[0].batterName, games, pa, ab, reached,
      onBasePct: pa > 0 ? reached / pa : 0,
      hits, bb, so, xbh, pitchesSeen,
      pitchesPerPa: pa > 0 ? pitchesSeen / pa : 0,
    });
  }
  return out;
}

export interface OrderSlot {
  slot: number;            // 1-based
  playerId: string;
  playerName: string;
  reason: string;          // bench-coach rationale
  thinSample: boolean;     // < MIN_PA at-bats — treat as a soft suggestion
}

export interface SuggestedOrder {
  slots: OrderSlot[];
  note: string;            // overall sample-honesty note
  unrankedPlayerIds: string[]; // in the lineup but no at-bat data yet
}

/** Two real strategies from the play-by-play:
 *  - 'onbase'   "Get Baserunners": stack hitters by how often they reach base.
 *  - 'weardown' "Wear Down Pitcher": front-load the bats that run up the pitch count
 *               (exploits youth pitch-count limits — force their ace out early). */
export type OrderMode = 'onbase' | 'weardown';

const MIN_PA = 6;
const f3 = (n: number) => n.toFixed(3).replace(/^0\./, '.');

/**
 * Suggest a batting order from on-base + production. Philosophy: "set the table,
 * then drive them in." Top on-base hitters lead off; the best extra-base bats hit
 * 3-4; everyone else by on-base. Players with no at-bat data are listed separately
 * rather than guessed at.
 *
 * @param lineupPlayerIds the players actually in the lineup (order is built for these)
 */
export function suggestBattingOrder(
  signals: Map<string, BatterSeasonSignal>,
  lineupPlayerIds: string[],
  nameById: (id: string) => string,
  mode: OrderMode = 'onbase',
): SuggestedOrder {
  const ranked = lineupPlayerIds.map(id => signals.get(id)).filter((s): s is BatterSeasonSignal => !!s && s.pa > 0);
  const unrankedPlayerIds = lineupPlayerIds.filter(id => !signals.get(id)?.pa);

  let ordered: BatterSeasonSignal[];
  let topXbh: BatterSeasonSignal | undefined;
  let hasProducer = false;

  if (mode === 'weardown') {
    // Front-load the bats that run up the count; on-base breaks ties.
    ordered = [...ranked].sort((a, b) => b.pitchesPerPa - a.pitchesPerPa || b.onBasePct - a.onBasePct);
  } else {
    // On-base, with patience as the tiebreaker.
    ordered = [...ranked].sort((a, b) => b.onBasePct - a.onBasePct || b.pitchesPerPa - a.pitchesPerPa);
    // Carve out a cleanup spot only when someone has REAL extra-base pop (≥2 XBH).
    topXbh = [...ranked].sort((a, b) => b.xbh - a.xbh)[0];
    hasProducer = !!topXbh && topXbh.xbh >= 2;
    if (hasProducer) {
      const idx = ordered.findIndex(s => s.playerId === topXbh!.playerId);
      if (idx > 3) { ordered.splice(idx, 1); ordered.splice(3, 0, topXbh!); }
    }
  }

  const slots: OrderSlot[] = ordered.map((s, i) => {
    let reason: string;
    if (mode === 'weardown') {
      reason = i === 0
        ? `Makes the pitcher work hardest: ${s.pitchesPerPa.toFixed(1)} pitches/PA`
        : `${s.pitchesPerPa.toFixed(1)} pitches/PA${s.bb ? `, ${s.bb} BB` : ''}`;
    } else if (i === 0) {
      reason = `Gets on base most: ${f3(s.onBasePct)} OBP, ${s.pitchesPerPa.toFixed(1)} pitches/PA`;
    } else if (hasProducer && s.playerId === topXbh!.playerId) {
      reason = `Team-leading ${s.xbh} extra-base hits, your run producer`;
    } else {
      reason = `On-base ${f3(s.onBasePct)}${s.xbh ? `, ${s.xbh} XBH` : ''}`;
    }
    return { slot: i + 1, playerId: s.playerId, playerName: nameById(s.playerId) || s.playerName, reason, thinSample: s.pa < MIN_PA };
  });

  const totalPa = ranked.reduce((sum, s) => sum + s.pa, 0);
  const games = Math.max(0, ...ranked.map(s => s.games));
  const sample = `${totalPa} plate appearances across ${games} game${games === 1 ? '' : 's'}`;
  const hedge = games < 3 ? ' Small sample: a starting point, not gospel.' : '';
  const note = ranked.length === 0
    ? 'No play-by-play at-bats yet. Import a game to get a data-driven order.'
    : mode === 'weardown'
      ? `Ordered by pitches seen per at-bat. Front-load the grinders to run up the pitch count. Based on ${sample}.${hedge}`
      : `Ordered by on-base rate. Based on ${sample}.${hedge}`;

  return { slots, note, unrankedPlayerIds };
}
